import { WorkspaceConfiguration } from '@citrite/workspace-ui-platform';
import { AnalyticsEvent } from 'analytics';
import { debounce } from 'lodash';
import { ResourceType } from 'App/Tile/ResourceDetails';
import { getAndResetShieldStateInformationForAnalytics } from 'Environment/BrowserExtension/browserExtensionBridge';
import { getAndResetShieldStateInformationForAnalytics as getShieldStateForChrome } from 'Environment/ChromeApp/ChromeAppBridge';
import { ShieldStateInfoPayload } from 'Environment/ChromeApp/types';
import { getClientType } from 'Environment/getClientType';
import { getFromLocalStorage, setInLocalStorage } from 'javascript/sf/Storage';
import { isResourceLeaseLaunchEnabled } from 'utils/resourceLeaseLaunchCheck';
import { isAdvancedWorkspaceResiliencyEnabled } from 'Workspace/advancedWorkspaceResiliency';
import { LeasingProperties } from 'Workspace/BrowserExtension/customerConfiguration';
import { Resource } from 'Workspace/ResourceProvider/resourceTypes';
import { getStoreUrl } from 'Workspace/SchemeCallHandler/schemeCallHandler';
import {
	getShieldLastLoggedInTimeKey,
	IShieldHeartBeatMetadata,
	ShieldHealthCheckPayload,
} from 'Workspace/TelemetryEvents/shieldHealthCheck/ShieldHealthCheckPayload';
import { TelemetryEventPublisher } from 'Workspace/TelemetryEvents/TelemetryEventTypes';

const LAST_SHIELD_HEARTBEAT_EVENT_TIMESTAMP_STORAGE_KEY =
	'lastShieldTelemetryHeartBeatEventsTime';
export const ONE_DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;
interface IShieldHeartBeatEvent {
	isExtensionAvailable?: boolean;
	CASEventPublisher: TelemetryEventPublisher;
	resources: Resource[];
	userDisplayName: string;
	userOid: string;
	storeGuid: string;
	userId: string;
	isChromeApp?: boolean;
	isShieldEnabled: boolean;
	cwaVersion?: string;
	deviceId?: string;
	supportsGetAndResetShieldStateInformationForAnalytics?: boolean;
	isUserLoggedIn: boolean;
}

interface IResourcesMetadata {
	totalApps: number;
	totalDesktops: number;
	leasableApps: number;
	leasableDesktops: number;
}

interface LeasesStateMetadata {
	clSyncTriggers: number;
	clSyncFailures: number;
	clSyncLastSuccess: number;
	totalLeaseFiles: number;
	totalExpiredFiles: number;
	clSyncExceptionCode?: string;
}

export const shouldSendHeartbeat = () => {
	const lastEventTimestamp = getFromLocalStorage<number>(
		LAST_SHIELD_HEARTBEAT_EVENT_TIMESTAMP_STORAGE_KEY
	);
	const currentTime = Date.now();
	return (
		!lastEventTimestamp || currentTime - lastEventTimestamp >= ONE_DAY_IN_MILLISECONDS
	);
};

function getResourcesMetadataForShieldTelemetry(
	resources: Resource[]
): IResourcesMetadata {
	return resources.reduce(
		(accumulatedResult, resource) => {
			const { type, clmetadata } = resource;
			if (type === ResourceType.Desktop) {
				accumulatedResult.totalDesktops++;
				if (clmetadata?.leasesupported) {
					accumulatedResult.leasableDesktops++;
				}
			} else {
				accumulatedResult.totalApps++;
				if (clmetadata?.leasesupported) {
					accumulatedResult.leasableApps++;
				}
			}
			return accumulatedResult;
		},
		{ totalApps: 0, totalDesktops: 0, leasableApps: 0, leasableDesktops: 0 }
	);
}

async function getLeasesStateForTelemetry(
	isChromeApp: boolean,
	workload: ShieldStateInfoPayload,
	isShieldEnabled: boolean
): Promise<LeasesStateMetadata> {
	const defaultValues = {
		clSyncTriggers: 0,
		clSyncFailures: 0,
		clSyncLastSuccess: 0,
		totalExpiredFiles: 0,
		totalLeaseFiles: 0,
	};
	if (!isShieldEnabled) {
		return defaultValues;
	}
	try {
		const { result } = isChromeApp
			? await getShieldStateForChrome(workload)
			: await getAndResetShieldStateInformationForAnalytics();
		return {
			clSyncTriggers: result?.clSyncTriggersCount ?? 0,
			clSyncFailures: result?.clSyncFailuresCount ?? 0,
			clSyncLastSuccess: result?.clSyncLastSuccessTime ?? 0,
			totalExpiredFiles: result?.totalExpiredFiles ?? 0,
			totalLeaseFiles: result?.totalLeaseFiles ?? 0,
			clSyncExceptionCode: result?.clSyncExceptionCode,
		};
	} catch (_e) {
		// In case of any error, return default values.
	}
	return defaultValues;
}

export async function createAndSendShieldHeartBeatEvent({
	isExtensionAvailable = false,
	CASEventPublisher,
	resources,
	userDisplayName,
	storeGuid,
	userId,
	isChromeApp = false,
	isShieldEnabled,
	cwaVersion,
	deviceId,
	supportsGetAndResetShieldStateInformationForAnalytics,
	userOid,
	isUserLoggedIn,
}: IShieldHeartBeatEvent): Promise<void> {
	if (isChromeApp && !supportsGetAndResetShieldStateInformationForAnalytics) {
		return;
	}
	const resourcesInfo = getResourcesMetadataForShieldTelemetry(resources);
	const leasesInfo = await getLeasesStateForTelemetry(
		isChromeApp,
		{
			storeId: storeGuid,
			userId,
		},
		isShieldEnabled
	);

	const metadata: IShieldHeartBeatMetadata = {
		extensionAvailable: isExtensionAvailable,
		storeConfigUrl: getStoreUrl(),
		userName: userDisplayName,
		shieldEnabled: isShieldEnabled,
		...resourcesInfo,
		...leasesInfo,
		cwaVersion,
		deviceId,
		userOid,
		timestamp: Date.now(),
		clientType: getClientType(),
		isUserLoggedIn,
		lastLoggedInTime:
			getFromLocalStorage<number>(getShieldLastLoggedInTimeKey(userId)) ?? undefined,
	};
	const event: AnalyticsEvent = ShieldHealthCheckPayload.createHeartBeatPayload(metadata);
	CASEventPublisher.publishEvent(event);
}

export function getIsShieldEnabled(
	workspaceConfiguration: WorkspaceConfiguration,
	leasingProperties?: LeasingProperties | null
): boolean {
	if (!isAdvancedWorkspaceResiliencyEnabled(workspaceConfiguration)) {
		return false;
	}

	if (!leasingProperties) {
		return false;
	}

	return isResourceLeaseLaunchEnabled(leasingProperties);
}

export const debouncedCreateAndSendShieldHeartBeatEvent = debounce(
	async (eventPayload: IShieldHeartBeatEvent) => {
		setInLocalStorage(LAST_SHIELD_HEARTBEAT_EVENT_TIMESTAMP_STORAGE_KEY, Date.now());
		await createAndSendShieldHeartBeatEvent(eventPayload);
	},
	60000,
	{ leading: false, trailing: true }
);
